import { AfterContentInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import * as calcite from 'calcite-web';
import { CookieService } from 'ngx-cookie';
import { Observable, Subscription } from 'rxjs';
import { skip } from 'rxjs/internal/operators/skip';

import { environmentOm } from '../../environment-om';
import { AoiToolComponent } from '../aoi-tool/aoi-tool.component';
import { DrawToolComponent } from '../draw-tool/draw-tool.component';
import { ICRS } from '../models';
import { SearchTipsComponent } from '../search-tips/search-tips.component';

@Component({
    selector: 'om-app-nav-menu',
    templateUrl: './nav-menu.component.html',
    styleUrls: ['./nav-menu.component.css']
})
export class NavMenuComponent implements OnInit, AfterContentInit {
    @ViewChild('layerCatalogWindow', { static: true }) private readonly layerCatalogWindowRef: ElementRef<any>;
    @ViewChild('layerCatalog', { static: true }) private readonly layerCatalogRef: ElementRef<any>;
    @ViewChild('mapCatalogWindow', { static: true }) private readonly mapCatalogWindowRef: ElementRef<any>;
    @ViewChild('mapCatalog', { static: true }) private readonly mapCatalogRef: ElementRef<any>;
    @ViewChild('mapMergeWindow', { static: true }) private readonly mapMergeWindowRef: ElementRef<any>;
    @ViewChild('mapMerge', { static: true }) private  readonly mapMergeRef: ElementRef<any>;
    @ViewChild('measureToolsWindow', { static: true }) private readonly measureToolsWindowRef: ElementRef<any>;
    @ViewChild('routingWindow', { static: true }) private readonly routingWindowRef: ElementRef<any>;
    @ViewChild('routing', { static: true }) private readonly routingRef: ElementRef<any>;
    @ViewChild('tabs', { static: true }) private readonly tabsRef: ElementRef<any>;
    @ViewChild('distance', { static: true }) private readonly distanceRef: ElementRef<any>;
    @ViewChild('area', { static: true }) private readonly areaRef: ElementRef<any>;
    @ViewChild('coordinates', { static: true }) private readonly coordinatesRef: ElementRef<any>;
    @ViewChild('search', { static: true }) private readonly searchRef: ElementRef<any>;
    @ViewChild('searchTips', { static: true }) private readonly searchTips: SearchTipsComponent;
    @ViewChild('drawToolWindow', { static: true }) private readonly drawToolWindow: ElementRef<any>;
    @ViewChild('drawTool', { static: true }) private readonly drawTool: DrawToolComponent;
    @ViewChild('reportsListWindow', { static: true }) private readonly ReportsListWindow: ElementRef<any>;
    @ViewChild('aboutWindow', { static: true }) private readonly aboutWindowRef: ElementRef<any>;
    @ViewChild('contactWindow', { static: true }) private readonly contactWindowRef: ElementRef<any>;
    @ViewChild('resetMapWindow', { static: true }) private readonly resetMapWindowRef: ElementRef<any>;
    @ViewChild('auth', { static: true }) private readonly authRef: ElementRef<any>;
    @ViewChild('bookmarksWindow', { static: true }) private readonly bookmarksWindowRef: ElementRef<any>;
    @ViewChild('bookmarks', { static: true }) private readonly bookmarksRef: ElementRef<any>;
    @ViewChild('userSettingsWindow', { static: true }) private readonly userSettingsWindowRef: ElementRef<any>;
    @ViewChild('userSettings', { static: true }) private readonly userSettingsRef: ElementRef<any>;
    @ViewChild('streetViewWindow', { static: true }) private readonly streetViewWindowRef: ElementRef<any>;
    @ViewChild('streetView', { static: true }) private readonly streetViewRef: ElementRef<any>;
    @ViewChild('videoLogWindow', { static: true }) private readonly videoLogWindowRef: ElementRef<any>;
    @ViewChild('videoLog', { static: true }) private readonly videoLogRef: ElementRef<any>;
    @ViewChild('videoLogController', { static: true }) private readonly videoLogControllerRef: ElementRef<any>;
    @ViewChild('petlWindow', { static: true }) private readonly petlWindowRef: ElementRef<any>;
    @ViewChild('petl', { static: true }) private readonly petlRef: ElementRef<any>;
    @ViewChild('calloutToolWindow', { static: true }) private readonly calloutToolWindowRef: ElementRef<any>;
    @ViewChild('calloutTool', { static: true }) private readonly calloutToolRef: ElementRef<any>;
    @ViewChild('rmsSelect', { static: true }) private readonly rmsSelectRef: ElementRef<any>;
    @ViewChild('latLongSelect', { static: true }) private readonly latLongSelectRef: ElementRef<any>;
    @ViewChild('shareLayerWindow', { static: true }) private readonly shareLayerWindowRef: ElementRef<any>;
    @ViewChild('shareLayer', { static: true }) private readonly shareLayerRef: ElementRef<any>;
    @ViewChild('unshareLayerWindow', { static: true }) private readonly unshareLayerWindowRef: ElementRef<any>;
    @ViewChild('unshareLayer', { static: true }) private readonly unshareLayerRef: ElementRef<any>;
    @ViewChild('aoiToolWindow', { static: true }) private readonly aoiToolWindowRef: ElementRef<any>;
    @ViewChild('aoiTool', { static: true }) private readonly aoiTool: AoiToolComponent;
    @ViewChild('disclaimerWindow', { static: true }) private readonly disclaimerWindowRef: ElementRef<any>;
    @ViewChild('errorWindow', { static: true }) private readonly errorWindowRef: ElementRef<any>;

    @Input() isLoggedIn = false;
    @Input() isCwopaUser = false;

    @Input() mapRef: any;
    @Input() openVL: Observable<{ countyCode: string; stateRouteNumber: string; segmentNumber: string; offset?: string | number }>;
    private openVLSubscription: Subscription;
    @Input() openSV: Observable<{ countyCode: string; stateRouteNumber: string; segmentNumber: string; offset?: string | number }>;
    private openSVSubscription: Subscription;
    @Input() openShareLayer: Observable<any>;
    private openShareLayerSubscription: Subscription;
    @Input() get isInternal() {
        return this._isInternal;
    }
    private _isInternal = false;

    maxHeight = 768;
    drawToolWidth = 500;
    drawToolTop = 100;
    markerSymbol = {
        type: 'simple-marker',
        style: 'circle',
        outline: {
            width: 2,
            color: 'red'
        }
    };

    pdauthURL = environmentOm.pdauthURL;

    reopenAOI = false;

    userName = 'no one';
    userPermissions: string[];
    searchTooltipsClickEventListenerAdded: boolean;

    errorMessage: string;

    private _showOverview = false;
    get showOverview() {
        return this._showOverview;
    }
    set showOverview(val) {
        this._showOverview = val;
        this.onShowOverview.emit(this._showOverview);
    }

    _streetViewDefaultLocation: ICRS;
    
    @Input() set streetViewDefaultLocation(value: ICRS) {
        this._streetViewDefaultLocation = value;

        if (value) {
            this.streetViewRef.nativeElement.autoPanMap = true;
        }
    }

    @Input() set isIdentifyDialogOpen(value: boolean) {
        if (value) {
            this.streetViewRef.nativeElement.openToDefaultLocation = false;
        } else {
            this.streetViewRef.nativeElement.openToDefaultLocation = true;
            this.streetViewRef.nativeElement.autoPanMap = false;
        }
    }

    @Output() readonly onResetPress: EventEmitter<void> = new EventEmitter<void>();
    @Output() readonly onResetConfirm: EventEmitter<void> = new EventEmitter<void>();
    @Output() readonly onShowOverview: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() readonly onMapOpened: EventEmitter<any> = new EventEmitter<any>();
    @Output() readonly onLogIn: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() readonly userSettingsChange: EventEmitter<any> = new EventEmitter<any>();
    @Output() readonly disableIdentify: EventEmitter<{ key: string; disable: boolean }> = new EventEmitter<{ key: string; disable: boolean }>();
    @Output() readonly openShareMap: EventEmitter<any[]> = new EventEmitter<any[]>();

    private authInit = false;

    constructor(private readonly router: Router, private readonly changeDectRef: ChangeDetectorRef, private readonly cookies: CookieService) { }

    ngOnInit() {
        this.aoiTool.done.subscribe(_ => this.closeAoiTool());
        this.searchRef.nativeElement.filterLayersBeforeAdd = true;

        this.setupAuth();

        this.addWindowEventListeners();

        this.mapCatalogWindowRef.nativeElement.addEventListener('openChange', open => {
            this.mapCatalogRef.nativeElement.active = open.detail;
        });

        this.mapCatalogRef.nativeElement.addEventListener('openmergemap', event => {
            this.mapCatalogWindowRef.nativeElement.close();
            this.mapMergeWindowRef.nativeElement.open();
            this.mapMergeRef.nativeElement.map = event.detail;
        });
        this.mapCatalogRef.nativeElement.addEventListener('opensharemap', maps => {
            this.mapCatalogWindowRef.nativeElement.close();
            this.openShareMap.emit(maps.detail);
        });
        this.mapMergeRef.nativeElement.addEventListener('ondone', event => {
            this.mapMergeWindowRef.nativeElement.close();
            if (event.detail) {
                this.mapOpened(event);
            }
        });

        this.mapMergeRef.nativeElement.addEventListener('oncancel', event => {
            this.mapMergeWindowRef.nativeElement.close();
        });
        this.drawToolWindow.nativeElement.addEventListener('openChange', open => {
            open.detail ? this.drawTool.onOpen() : this.drawTool.onClose();
            this.deactivateIdentifyWhenOpen({ key: 'draw', disable: open.detail });
        });
        this.routingWindowRef.nativeElement.addEventListener('openChange', open => {
            this.routingRef.nativeElement.active = open.detail;
            this.deactivateIdentifyWhenOpen({ key: 'route', disable: open.detail });
        });

        this.bookmarksWindowRef.nativeElement.addEventListener('openChange', open => {
            this.bookmarksRef.nativeElement.active = open.detail;
        });
        this.userSettingsWindowRef.nativeElement.addEventListener('openChange', open => {
            this.userSettingsRef.nativeElement.active = open.detail;
        });
        this.userSettingsRef.nativeElement.addEventListener('activeChange', event => {
            if (event.detail === false) {
                this.userSettingsWindowRef.nativeElement.close();
            }
        });
        this.userSettingsRef.nativeElement.addEventListener('settingsChange', event => {
            this.userSettingsChange.emit(event.detail);
        });
        this.videoLogWindowRef.nativeElement.addEventListener('openChange', open => {
            this.videoLogRef.nativeElement.active = open.detail;
        });
        this.videoLogWindowRef.nativeElement.addEventListener('stateChange', (state: CustomEvent<'minimized' | 'maximized' | 'default'>) => {
            const videoLogService = this.videoLogControllerRef.nativeElement.VideoLogService;
            if (state.detail === 'maximized' && !videoLogService.isFullSize) {
                videoLogService.toggleFullsizeImages();
            }
        });
        this.openVLSubscription = this.openVL.subscribe((CRS) => {
            const timeout = 750;
            this.videoLogWindowRef.nativeElement.open();
            // wait for videolog to load it previous spot, then overwrite it with our parameters.
            setTimeout(() => {
                this.videoLogRef.nativeElement.setCrs(CRS);
            }, timeout);
        });
        this.openSVSubscription = this.openSV.subscribe((CRS) => {
            this.streetViewWindowRef.nativeElement.open();

            // Wait for street-view component to activate
            setTimeout(() => {
                this.streetViewRef.nativeElement.setRms(CRS);
            }, 500);
        });
        this.calloutToolWindowRef.nativeElement.addEventListener('openChange', open => {
            this.calloutToolRef.nativeElement.active = open.detail;
            this.deactivateIdentifyWhenOpen({ key: 'callout', disable: open.detail });
        });
        this.petlWindowRef.nativeElement.addEventListener('openChange', open => {
            this.petlRef.nativeElement.active = open.detail;
            if (open.detail) {
                this.petlRef.nativeElement.initialize();
            }
        });
        this.measureToolsWindowRef.nativeElement.addEventListener('openChange', open => {
            this.deactivateIdentifyWhenOpen({ key: 'measure', disable: open.detail });
            if (!open.detail) {
                this.distanceRef.nativeElement.active = false;
                this.areaRef.nativeElement.active = false;
                this.coordinatesRef.nativeElement.active = false;
            }
        });

        this.streetViewWindowRef.nativeElement.addEventListener('openChange', open => {
            this.streetViewRef.nativeElement.active = open.detail;
        });
        this.streetViewRef.nativeElement.addEventListener('positionChange', (position) => {
            this.latLongSelectRef.nativeElement.setAttribute('lat', position.detail.latitude);
            this.latLongSelectRef.nativeElement.setAttribute('long', position.detail.longitude);
            this.rmsSelectRef.nativeElement.position = position.detail;
        });
        this.latLongSelectRef.nativeElement.addEventListener('latChange', (lat) => {
            this.streetViewRef.nativeElement.setAttribute('lat', lat.detail);
        });
        this.latLongSelectRef.nativeElement.addEventListener('longChange', (long) => {
            this.streetViewRef.nativeElement.setAttribute('long', long.detail);
        });
        this.rmsSelectRef.nativeElement.addEventListener('rmsChange', (rms) => {
            this.streetViewRef.nativeElement.setRms(rms.detail);
        });
        // Trigger loading user settings
        const userSettingsChangeTempListener = (settings) => {
            this.userSettingsRef.nativeElement.removeEventListener('settingsChange', userSettingsChangeTempListener);
            this.userSettingsRef.nativeElement.active = false;
        };
        this.userSettingsRef.nativeElement.addEventListener('settingsChange', userSettingsChangeTempListener);
        this.userSettingsRef.nativeElement.active = true;
        this.layerCatalogRef.nativeElement.addEventListener('openShareLayers', (layerId) => {
            // close layerCatalog and open share layers
            this.layerCatalogWindowRef.nativeElement.close();
            this.openShareLayerComponent(layerId.detail);
        });
        this.openShareLayerSubscription = this.openShareLayer.subscribe((layerId: number) => {
            this.openShareLayerComponent(layerId);
        });
        this.shareLayerRef.nativeElement.addEventListener('onclose', () => {
            this.shareLayerWindowRef.nativeElement.close();
        });
        this.shareLayerWindowRef.nativeElement.addEventListener('openChange', (open) => {
            if (!open.detail) {
                this.shareLayerRef.nativeElement.userIds = [];
                this.shareLayerRef.nativeElement.recipients = [];
                this.shareLayerRef.nativeElement.alreadySharedNames = [];
                this.shareLayerWindowRef.nativeElement.active = false;
            }
        });

        this.layerCatalogRef.nativeElement.addEventListener('openUnshareLayers', (layerId) => {
            // close layerCatalog and open unshare layers
            this.layerCatalogWindowRef.nativeElement.close();
            this.unshareLayerRef.nativeElement.layerId = null;
            this.unshareLayerRef.nativeElement.layerId = layerId.detail;
            this.unshareLayerWindowRef.nativeElement.open();
        });
        this.unshareLayerRef.nativeElement.addEventListener('onclose', () => {
            this.unshareLayerRef.nativeElement.layerId = null;
            this.unshareLayerWindowRef.nativeElement.close();
        });

        this.setupDisclaimer();
    }

    setupAuth() {
        this.authRef.nativeElement.ready.then(() => {
            this.authRef.nativeElement.user.pipe(skip(1)).subscribe(user => {
                if (user) {
                    this.userName = user.fullName;
                    this.isLoggedIn = true;
                    this.onLogIn.emit(true);
                } else {
                    this.userName = 'no one';
                    this.isLoggedIn = false;
                    this.onLogIn.emit(false);
                }

                if (this.authInit) {
                    this.resetMap();
                }
                this.authInit = true;

                this.changeDectRef.detectChanges(); // Needed because AOI log in button doesn't update on actual login action for some reason
            });
        });

        this.authRef.nativeElement.permissions.subscribe(permissions => {
            this.isCwopaUser = permissions && permissions.includes('View CWOPA Data');
            this.userPermissions = permissions;
        });

        this.authRef.nativeElement.isOnInternalNetwork.subscribe(internal => {
            this._isInternal = internal;
        });

    }

    addWindowEventListeners() {
        window.addEventListener('openAoi', event => {
            if ((event as CustomEvent).detail) {
                if (this.disclaimerWindowRef.nativeElement.isOpened) {
                    this.reopenAOI = true;
                } else {
                    this.openAoiTool();
                }
            }
        });

        window.addEventListener('notAuthorized', (event: CustomEvent) => {
            this.errorMessage = event.detail.message;
            this.errorWindowRef.nativeElement.open();
        });
    }

    setupDisclaimer() {
        let disclaimerCookie = this.cookies.getObject('onemap_disclaimer_accepted');

        if (disclaimerCookie && typeof disclaimerCookie === 'boolean') {
            this.cookies.remove('onemap_disclaimer_accepted');
            disclaimerCookie = undefined;
        }

        if (!disclaimerCookie) {
            if (this.aoiToolWindowRef.nativeElement.isOpened) {
                this.reopenAOI = true;
                this.aoiToolWindowRef.nativeElement.close();
            }
            this.disclaimerWindowRef.nativeElement.open();
        }
    }

    acceptDisclaimer() {
        const nowDate = new Date();
        const expireDate = new Date(nowDate.setFullYear(nowDate.getFullYear() + 1));
        this.cookies.putObject('onemap_disclaimer_accepted', { accepted: true, date: nowDate.valueOf() }, { expires: expireDate });
        this.disclaimerWindowRef.nativeElement.close();
        if (this.reopenAOI) {
            this.openAoiTool();
            this.reopenAOI = false;
        }
    }

    get loggedIn(): boolean {
        return this.isLoggedIn;
    }

    get rms() {
        return (this.mapRef && this.mapRef.map && this.mapRef.map.mapcoreProps && this.mapRef.map.mapcoreProps.rms);
    }

    get rmsSnippet() {
        if (this.rms) {
            const rms = this.rms;
            const parts = [rms.countyCode, rms.stateRoute, rms.segmentBegin].filter(x => x);
            return `: ${parts.join(' | ')}`;
        }
        return '';
    }

    openResetMap() {
        this.onResetPress.emit();
        this.resetMapWindowRef.nativeElement.open();
    }

    resetMap() {
        this.onResetConfirm.emit();
        this.mapRef.MapService.resetMap();
        this.closeResetMap();

        this.closeWindowsByRef([
            this.streetViewWindowRef,
            this.videoLogWindowRef,
            this.streetViewWindowRef,
            this.videoLogWindowRef,
            this.measureToolsWindowRef,
            this.routingWindowRef,
            this.drawToolWindow,
            this.shareLayerWindowRef,
            this.unshareLayerWindowRef,
            this.layerCatalogWindowRef,
            this.mapCatalogWindowRef,
            this.aboutWindowRef,
            this.contactWindowRef,
            this.petlWindowRef,
            this.bookmarksWindowRef,
            this.userSettingsWindowRef,
            this.calloutToolWindowRef]);

        this.router.navigate(['/']);
        this.searchRef.nativeElement.text = '';

        this.aoiTool.reset();
        this.openAoiTool();
    }

    closeWindowsByRef(windowRef) {
        if (windowRef) {
            if (Array.isArray(windowRef)) {
                windowRef.forEach(r => this.closeWindowsByRef(r));
            } else {
                windowRef.nativeElement.close();
            }
        }
    }

    getMapFromPicker() {
        const map = this.mapRef.map;
        if (map) {
            return map;
        }
        return null;
    }
    closeResetMap() {
        this.resetMapWindowRef.nativeElement.close();
    }

    deactivateIdentifyWhenOpen(value: { key: string; disable: boolean }) {
        return this.disableIdentify.emit(value);
    }

    openLayerCatalog(event) {
        this.layerCatalogWindowRef.nativeElement.open();
        this.layerCatalogRef.nativeElement.active = true;
    }

    layerCatalogWindowOpenChange(e: CustomEvent<boolean>) {
        if (!e.detail) {
            this.layerCatalogRef.nativeElement.active = false;
        }
    }

    layersAddedFromCatalog(e: CustomEvent<boolean>) {
        this.layerCatalogWindowRef.nativeElement.close();
    }

    openMapCatalog(event) {
        this.mapCatalogRef.nativeElement.mergeMaps = false;
        this.mapCatalogWindowRef.nativeElement.open();
    }

    closeMapCatalog(event) {
        this.mapCatalogWindowRef.nativeElement.close();
    }

    mapOpened(e) {
        this.onMapOpened.emit(e);
    }

    openAboutWindow() {
        this.aboutWindowRef.nativeElement.open();
    }

    openContactWindow() {
        this.contactWindowRef.nativeElement.open();
    }

    getRmsUrl(baseUrl: string) {
        let url = baseUrl;
        if (this.rms) {
            url += `?${this.rms.params}`;
        }
        return url;
    }

    closeContactWindow() {
        this.contactWindowRef.nativeElement.close();
    }

    closeErrorWindow() {
        this.errorWindowRef.nativeElement.close();
    }

    ngAfterContentInit() {
        calcite.dropdown();
    }

    onTabChanged(e) {
        const refs = {
            Area: this.areaRef,
            Coordinates: this.coordinatesRef,
            Distance: this.distanceRef
        };
        for (const ref of Object.keys(refs)) {
            refs[ref].nativeElement.active = ref === e.detail.name;
        }
    }

    onWindowOpenChange(e) {
        if (!e.detail) {
            this.areaRef.nativeElement.active = false;
            this.distanceRef.nativeElement.active = false;
            this.coordinatesRef.nativeElement.active = false;
        }
    }

    logInOrOut(e: MouseEvent) {
        e.preventDefault();
        if (this.isLoggedIn) {
            this.authRef.nativeElement.logout();
        } else {
            this.authRef.nativeElement.login();
        }
    }

    openBookmarks(e: MouseEvent) {
        e.preventDefault();
        this.bookmarksWindowRef.nativeElement.open();
    }
    openUserSettings(e: MouseEvent) {
        e.preventDefault();
        this.userSettingsWindowRef.nativeElement.open();
    }

    openStreetView(e: MouseEvent) {
        e.preventDefault();
        this.streetViewWindowRef.nativeElement.open();

        if (this._streetViewDefaultLocation) {
            this.streetViewRef.nativeElement.setRms(this._streetViewDefaultLocation);
        }
    }

    openVideoLog(e: MouseEvent) {
        e.preventDefault();
        this.videoLogWindowRef.nativeElement.open();

        if (this._streetViewDefaultLocation) {
            this.videoLogRef.nativeElement.setCrs(this._streetViewDefaultLocation);
        }
    }

    openCalloutTool(e: MouseEvent) {
        e.preventDefault();
        this.calloutToolWindowRef.nativeElement.open();
    }
    openTIRe(e: MouseEvent) {
        e.preventDefault();
        (<any>window).open(this.getRmsUrl('/tire'));
    }

    openPetl(e: MouseEvent) {
        e.preventDefault();
        this.petlWindowRef.nativeElement.open();
    }

    closePetl(e: MouseEvent) {
        e.preventDefault();
        this.petlWindowRef.nativeElement.close();
    }

    openMeasureTools(e: MouseEvent) {
        e.preventDefault();
        this.measureToolsWindowRef.nativeElement.open();
        this.tabsRef.nativeElement.activateTab('Distance');
        this.distanceRef.nativeElement.active = true;
    }

    openRouting(e: MouseEvent) {
        e.preventDefault();
        this.routingWindowRef.nativeElement.open();
    }

    openDrawTool(e: MouseEvent) {
        e.preventDefault();
        this.drawToolWindow.nativeElement.open();
    }

    openAoiTool() {
        this.aoiTool.activateTab();
        this.aoiToolWindowRef.nativeElement.open();
    }

    closeAoiTool() {
        this.aoiToolWindowRef.nativeElement.close();
    }

    openReportsList() {
        this.ReportsListWindow.nativeElement.open();
    }

    toggleSearchTips($event) {
        if (!this.searchTooltipsClickEventListenerAdded) {
            // Add an event listener to that any click will close the tooltips.
            document.addEventListener('click', (event: any) => {
                if (this.searchTips.visible && event.target && event.target.tagName && event.target.tagName !== 'MC-SEARCH') {
                    this.searchTips.visible = false;
                }
            });
            this.searchTooltipsClickEventListenerAdded = true;
        }
        this.searchTips.visible = !this.searchTips.visible;
        $event.stopPropagation();
    }

    tutorialVideo() {
        window.open('./static/OneMap Guide.mp4', '_blank');
    }

    searchResultSelected(event) {
        if (event.detail.type === 'map') {
            this.onMapOpened.emit(event.detail.result);
        }
    }

    @Input() mergeMap() {
        this.mapCatalogRef.nativeElement.mergeMaps = true;
        this.mapCatalogWindowRef.nativeElement.open();
    }

    // Disallow user to bypass user agreement for using Esc key
    @HostListener('keydown', ['$event'])
    public keydown(e: any): void {
        const escKey = 27;
        if (e.keyCode === escKey) {
            this.disclaimerWindowRef.nativeElement.open(NavMenuComponent, { disableClose: true });
        }
    }

    openShareLayerComponent(layerId: number): void {
        this.shareLayerRef.nativeElement.layerId = null;
        this.shareLayerRef.nativeElement.layerId = layerId;
        this.shareLayerRef.nativeElement.includeEmail = true;
        this.shareLayerWindowRef.nativeElement.open();
    }
}
